import { baseThunkConfig, createAppSlice } from '@exp/redux/createAppSlice';

import { getProfileStrength, getProfileUser, getSocialConnections } from '../api/profile';
import { patchPersonalInfo } from '../api/sign-on';
import { getPublicUserLookup, putUserDemographic } from '../api/user';

const EXCLUDED_PROMPTS = ['affiliation_email'];

/**
 * User Slice
 */
const userSlice = createAppSlice({
  initialState: {
    existingUsers: [],
    socialConnections: null,
    user: null,
  },
  name: 'user',
  reducers: (create) => ({
    checkForUsername: create.asyncThunk((searchTerm) => getPublicUserLookup(searchTerm), {
      ...baseThunkConfig,
      fulfilled: (state, action) => {
        state.existingUsers = state.existingUsers.concat([action.payload]);
      },
    }),

    fetchProfileUser: create.asyncThunk((uuid) => getProfileUser(uuid), baseThunkConfig),

    fetchProfileStrength: create.asyncThunk(async () => {
      const data = await getProfileStrength();
      return {
        ...data,
        actions: (data.prompts || []).filter((prompt) => !EXCLUDED_PROMPTS.includes(prompt.type) && prompt.title),
        completeness: data?.completeness || 0,
        isExcellent: data?.completeness === 100,
        isStrong: data?.completeness >= 75 && data?.completeness < 100,
        isMedium: data?.completeness >= 50 && data?.completeness < 75,
        isWeak: data?.completeness < 50,
      };
    }, baseThunkConfig),

    fetchSocialConnections: create.asyncThunk(
      async () => {
        const data = await getSocialConnections();
        return data?.map((c) => ({
          ...c,
          displayName: c.externalProfileName || (c.username ? `@${c.username}` : ''),

          // Format the dates to be more user-friendly
          connectedDate: c.connectedTime ? new Date(c.connectedTime).toLocaleDateString() : null,
          expirationDate: c.expirationTime ? new Date(c.expirationTime).toLocaleDateString() : null,
        }));
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          state.socialConnections = action.payload;
        },
      },
    ),
    saveDemographics: create.asyncThunk(
      async (data = {}) => {
        const payload = {
          userId: data.userId,
          birthDate: data.birthDate,
        };
        await putUserDemographic(payload);
        return payload;
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          state.user = { ...state.user, ...action.payload };
        },
      },
    ),
    savePersonalInfo: create.asyncThunk(
      async (data = {}) => {
        const payload = {
          birthDate: data.birthDate,
          countryCode: data.country?.alpha2,
          countryCodeId: data.country?.id,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          state: data.state?.code,
          subdivisionId: data.state?.subdivisionId,
          interest: data?.interest,
        };
        await patchPersonalInfo(payload);
        return payload;
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          state.user = { ...state.user, ...action.payload };
        },
      },
    ),
  }),
  selectors: {
    selectExistingUsers: (state) => state.existingUsers,
    selectSocialConnections: (state) => state.socialConnections,
    selectUser: (state) => state.user,
  },
});

export const {
  checkForUsername,
  fetchProfileUser,
  fetchProfileStrength,
  fetchSocialConnections,
  saveDemographics,
  savePersonalInfo,
} = userSlice.actions;

export const { selectExistingUsers, selectSocialConnections, selectUser } = userSlice.selectors;

export default userSlice;
